'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Loader2, Mic, CheckCircle2, AlertCircle, HelpCircle, Tag, Hash } from 'lucide-react';

type SpotlightResult = {
  id: string;
  type: 'meeting' | 'segment' | 'item' | 'entity';
  title: string;
  subtitle?: string;
  href: string;
  icon: 'meeting' | 'decision' | 'action' | 'risk' | 'question' | 'segment' | 'entity';
};

const ICONS = {
  meeting: Mic,
  decision: CheckCircle2,
  action: Mic,
  risk: AlertCircle,
  question: HelpCircle,
  segment: Hash,
  entity: Tag,
} as const;

function formatTs(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function Spotlight() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SpotlightResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hotkey global Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // Reset au close, focus au open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
      setResults([]);
      setActiveIdx(0);
    }
  }, [open]);

  // Debounced fetch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!open || query.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/v1/search?q=${encodeURIComponent(query)}&limit=5`);
        if (res.ok) {
          const json = await res.json();
          const flat: SpotlightResult[] = [];
          for (const m of json.results.meetings ?? []) {
            flat.push({
              id: `m-${m.id}`,
              type: 'meeting',
              title: m.title,
              subtitle: new Date(m.date).toLocaleDateString('fr-FR'),
              href: m.href,
              icon: 'meeting',
            });
          }
          for (const it of json.results.items ?? []) {
            flat.push({
              id: `i-${it.id}`,
              type: 'item',
              title: it.text,
              subtitle: `${it.kind} · [${formatTs(it.ts_in)}]${it.meeting_title ? ' · ' + it.meeting_title : ''}`,
              href: it.href,
              icon: it.kind as 'decision' | 'action' | 'risk' | 'question',
            });
          }
          for (const s of json.results.segments ?? []) {
            flat.push({
              id: `s-${s.id}`,
              type: 'segment',
              title: s.snippet,
              subtitle: `[${formatTs(s.ts_in)}]${s.meeting_title ? ' · ' + s.meeting_title : ''}`,
              href: s.href,
              icon: 'segment',
            });
          }
          for (const e of json.results.entities ?? []) {
            flat.push({
              id: `e-${e.id}`,
              type: 'entity',
              title: e.name,
              subtitle: e.kind,
              href: e.href,
              icon: 'entity',
            });
          }
          setResults(flat);
          setActiveIdx(0);
        }
      } finally {
        setLoading(false);
      }
    }, 150);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open]);

  const navigate = useCallback(
    (href: string) => {
      setOpen(false);
      router.push(href);
    },
    [router],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(0, results.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && results[activeIdx]) {
      e.preventDefault();
      navigate(results[activeIdx].href);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Recherche rapide"
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 p-4 pt-[10vh] backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && setOpen(false)}
    >
      <div className="w-full max-w-xl overflow-hidden rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] shadow-2xl">
        <div className="relative border-b border-[var(--color-border-subtle)]">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-fg-tertiary)]" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Cherche un mot, une décision, une personne…"
            aria-label="Recherche rapide"
            className="w-full bg-transparent py-4 pl-11 pr-12 text-base outline-none placeholder:text-[var(--color-fg-tertiary)]"
          />
          {loading && (
            <Loader2 className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-[var(--color-fg-tertiary)]" />
          )}
        </div>

        <div className="max-h-[50vh] overflow-y-auto" role="listbox" aria-label="Résultats">
          {results.length === 0 && query.trim().length >= 2 && !loading && (
            <p className="p-6 text-center text-sm text-[var(--color-fg-tertiary)]">
              Rien trouvé pour « {query} ».
            </p>
          )}
          {results.length === 0 && query.trim().length < 2 && (
            <div className="p-6 text-center text-xs text-[var(--color-fg-tertiary)]">
              <p>Tape pour rechercher.</p>
              <p className="mt-2">
                <kbd className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-secondary)] px-1.5 py-0.5 font-mono">↑↓</kbd>{' '}
                naviguer ·{' '}
                <kbd className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-secondary)] px-1.5 py-0.5 font-mono">⏎</kbd>{' '}
                ouvrir ·{' '}
                <kbd className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-secondary)] px-1.5 py-0.5 font-mono">esc</kbd>{' '}
                fermer
              </p>
            </div>
          )}
          {results.length > 0 && (
            <ul>
              {results.map((r, idx) => {
                const Icon = ICONS[r.icon];
                const active = idx === activeIdx;
                return (
                  <li key={r.id} role="option" aria-selected={active}>
                    <Link
                      href={r.href}
                      onClick={() => setOpen(false)}
                      onMouseEnter={() => setActiveIdx(idx)}
                      className={[
                        'flex items-start gap-3 px-4 py-3 transition-colors',
                        active
                          ? 'bg-[var(--color-brand-subtle)]'
                          : 'hover:bg-[var(--color-bg-secondary)]',
                      ].join(' ')}
                    >
                      <Icon
                        className={[
                          'mt-0.5 h-4 w-4 flex-shrink-0',
                          active ? 'text-[var(--color-brand)]' : 'text-[var(--color-fg-tertiary)]',
                        ].join(' ')}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{r.title}</p>
                        {r.subtitle && (
                          <p className="truncate text-xs text-[var(--color-fg-tertiary)]">{r.subtitle}</p>
                        )}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-[var(--color-border-subtle)] bg-[var(--color-bg-secondary)] px-4 py-2 text-xs text-[var(--color-fg-tertiary)]">
          <span>Stenos search</span>
          <Link
            href={`/search${query ? `?q=${encodeURIComponent(query)}` : ''}`}
            onClick={() => setOpen(false)}
            className="hover:text-[var(--color-fg-primary)]"
          >
            Voir tous les résultats →
          </Link>
        </div>
      </div>
    </div>
  );
}
